'use client';

/**
 * Sortable / filterable table column headers, and the value-as-filter link that
 * pairs with them.
 *
 * Extracted from the Applications page, which invented this pattern, so a
 * second table (Call Reports) can wear the same one instead of copying it. The
 * two tables render through DIFFERENT table components — Applications owns a
 * bespoke `<table>`, Call Reports uses the shared DataTable — so the pieces are
 * split accordingly:
 *
 *   ColumnHeaderContent  the label + sort toggle + filter popover, and NOTHING
 *                        around it. Goes inside whatever cell the host table
 *                        already renders (DataTable supplies its own <th>).
 *   ColumnHeaderCell     the same thing wrapped in a <th>, for a table that
 *                        expects the header to bring its own cell.
 *
 * Keeping them one component with two wrappers is the point: a divergence here
 * shows up as two tables whose headers behave subtly differently, which is
 * exactly the drift the shared design system exists to prevent.
 */

import { useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, Filter, X } from 'lucide-react';
import { Button, IconButton } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export type SortDir = 'asc' | 'desc';

export interface ColumnHeaderProps<TSortKey extends string = string> {
  label: string;
  /** Omit to make the column unsortable (the label renders as plain text). */
  sortKey?: TSortKey;
  currentSort?: TSortKey | null;
  currentDir?: SortDir;
  onSort?: (key: TSortKey) => void;
  /** Drives the funnel's filled/tinted state — set it from the APPLIED value. */
  filterActive?: boolean;
  /**
   * Filter body. The render-prop form receives `close()` so Apply/Clear can
   * dismiss the popover — without it the user gets no confirmation that their
   * click landed. Mutually exclusive with `filterPopover`.
   */
  filterPopover?: ReactNode;
  renderFilter?: (ctx: { close: () => void }) => ReactNode;
  align?: 'left' | 'center' | 'right';
}

const ALIGNMENT_CLASS: Record<NonNullable<ColumnHeaderProps['align']>, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

/** The controls only — no cell wrapper. Use inside a header cell the host renders. */
export function ColumnHeaderContent<TSortKey extends string = string>({
  label, sortKey, currentSort, currentDir = 'desc', onSort,
  filterActive, filterPopover, renderFilter, align = 'left',
}: ColumnHeaderProps<TSortKey>) {
  const sortable = !!sortKey && !!onSort;
  const isSorted = sortable && currentSort === sortKey;
  const [popoverOpen, setPopoverOpen] = useState(false);
  return (
    <div className={cn(
      'inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground',
      align === 'center' && 'justify-center',
    )}>
      {sortable ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onSort!(sortKey!)}
          // aria-sort belongs on the cell, but the accessible NAME of the
          // control has to say what activating it does, or a screen-reader user
          // hears only the column label on a button.
          aria-label={`Sort by ${label}${isSorted ? (currentDir === 'asc' ? ', currently ascending' : ', currently descending') : ''}`}
          className="-ml-2 min-h-11 gap-1 px-2 py-0 hover:bg-transparent hover:text-foreground"
        >
          {label}
          {isSorted && (currentDir === 'asc'
            ? <ArrowUp aria-hidden="true" className="w-3 h-3" />
            : <ArrowDown aria-hidden="true" className="w-3 h-3" />
          )}
        </Button>
      ) : <span>{label}</span>}
      {(filterPopover || renderFilter) && (
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <IconButton
              label={filterActive ? `${label} filter active` : `Filter ${label}`}
              variant="ghost"
              className={cn(
                'h-11 w-11 rounded-control p-0',
                filterActive ? 'text-primary' : 'text-muted-foreground/60',
              )}
            >
              <Filter aria-hidden="true" className="w-3 h-3" fill={filterActive ? 'currentColor' : 'none'} />
            </IconButton>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64">
            {renderFilter ? renderFilter({ close: () => setPopoverOpen(false) }) : filterPopover}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

/** ColumnHeaderContent in its own <th> — for tables that don't render one. */
export function ColumnHeaderCell<TSortKey extends string = string>(props: ColumnHeaderProps<TSortKey>) {
  const { align = 'left' } = props;
  return (
    <th
      className={cn('px-4 py-3', ALIGNMENT_CLASS[align])}
      // Announced by screen readers as the column's sort state. Only meaningful
      // on the column actually being sorted; 'none' on the rest would claim
      // every column is sortable.
      aria-sort={
        props.sortKey && props.currentSort === props.sortKey
          ? (props.currentDir === 'asc' ? 'ascending' : 'descending')
          : undefined
      }
    >
      <ColumnHeaderContent {...props} />
    </th>
  );
}

// ---------------------------------------------------------------------------
// Filter bodies
// ---------------------------------------------------------------------------

/** Single free-text "contains" filter. Enter applies. */
export function TextFilterPopover({
  value, onChange, placeholder, onApply, onClear, label = 'Filter', suggestions, suggestionsLoading, onPick,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  /** The wrapper closes the popover; these only have to apply the value. */
  onApply: () => void;
  onClear: () => void;
  label?: string;
  /**
   * Type-ahead values for this column. Omit for a plain text filter — the
   * component is unchanged in that case, which is what lets tables adopt the
   * suggestions one column at a time.
   *
   * A picked suggestion applies IMMEDIATELY rather than filling the box and
   * waiting for Apply: the value came from the data, so there is no partial
   * state left to confirm, and a second click to commit a value the user just
   * chose from a list reads as the click not having landed. That commit is the
   * host's to make, via `onPick` — see below.
   */
  suggestions?: string[];
  suggestionsLoading?: boolean;
  /**
   * Called when the reader PICKS a suggestion (click, or Enter on a highlighted
   * one); when absent, picks fall back to `onChange` as before. Typed input
   * never reaches it — that goes to `onChange` only — which is the point: a host
   * that applies on a pick cannot tell "chose 'TCS' from the list" from "typed
   * T, C, S" if both arrive through `onChange`, and would apply and close the
   * popover mid-word.
   */
  onPick?: (value: string) => void;
}) {
  const hasTypeahead = suggestions !== undefined;
  // Keyboard selection index into `suggestions`; -1 means "the input itself",
  // so Enter still applies whatever is typed rather than a highlighted row the
  // user never moved onto.
  const [active, setActive] = useState(-1);
  const list = suggestions ?? [];
  const pick = onPick ?? onChange;

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (active >= 0 && active < list.length) pick(list[active]!);
      else onApply();
      return;
    }
    if (!list.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1 >= list.length ? -1 : i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i <= -1 ? list.length - 1 : i - 1));
    } else if (e.key === 'Escape' && active >= 0) {
      e.preventDefault();
      setActive(-1);
    }
  }

  return (
    <div className="space-y-3">
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</div>
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => { onChange(e.target.value); setActive(-1); }}
        onKeyDown={onKeyDown}
        autoFocus
        role={hasTypeahead ? 'combobox' : undefined}
        aria-expanded={hasTypeahead ? list.length > 0 : undefined}
        aria-autocomplete={hasTypeahead ? 'list' : undefined}
        aria-activedescendant={active >= 0 ? `col-filter-opt-${active}` : undefined}
      />
      {hasTypeahead && (
        // Fixed-height region, so the Clear/Apply buttons under it do not jump
        // as suggestions arrive and narrow — a moving Apply button is how a
        // click lands on the wrong control.
        <div className="max-h-48 min-h-[2rem] overflow-y-auto" role="listbox" aria-label={`${label} suggestions`}>
          {list.map((s, i) => (
            <button
              key={s}
              id={`col-filter-opt-${i}`}
              type="button"
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(s)}
              className={cn(
                'block w-full truncate rounded-control px-2 py-1.5 text-left text-sm hover:bg-muted',
                i === active && 'bg-muted',
              )}
              title={s}
            >
              {s}
            </button>
          ))}
          {!list.length && (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              {suggestionsLoading ? 'Searching…' : value ? 'No matches — Apply still filters by what you typed.' : 'No values to suggest.'}
            </p>
          )}
        </div>
      )}
      <div className="flex justify-between gap-2">
        <Button size="sm" variant="ghost" onClick={onClear}>Clear</Button>
        <Button size="sm" onClick={onApply}>Apply</Button>
      </div>
    </div>
  );
}

/**
 * Pick-one-of-a-list filter (direction, status…).
 *
 * Applies on click rather than behind an Apply button: there is no partial
 * state to confirm, and a two-click select would be slower than the popover it
 * replaced.
 */
export function ChoiceFilterPopover({ label, options, value, onSelect, allLabel = 'All' }: {
  label: string;
  options: { value: string; label: string }[];
  /** The APPLIED value; null/'' means unfiltered. */
  value: string | null | undefined;
  onSelect: (value: string | null) => void;
  allLabel?: string;
}) {
  const active = value || null;
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="flex flex-col">
        {[{ value: '', label: allLabel }, ...options].map((o) => {
          const isActive = (o.value || null) === active;
          return (
            <Button
              key={o.value || '__all__'}
              type="button"
              variant="ghost"
              size="sm"
              aria-pressed={isActive}
              onClick={() => onSelect(o.value || null)}
              className={cn(
                'min-h-11 justify-start font-normal',
                isActive && 'bg-muted font-medium text-foreground',
              )}
            >
              {o.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Value-as-filter
// ---------------------------------------------------------------------------

/**
 * A cell value that filters the table to itself when clicked.
 *
 * A real `button`, not a styled span with onClick: it is an action, it has to
 * be keyboard-reachable, and a screen reader has to hear what it does rather
 * than just the recruiter's name. Clicking an ALREADY-applied value clears it,
 * so the same control is the way back out — otherwise the only escape from a
 * filter you set by clicking is a header popover you may not know exists.
 */
export function FilterValueButton({ value, active, onToggle, columnLabel, className, children, emptyHint }: {
  /** The value this cell would filter by. Nothing renders when it is empty. */
  value: string | null | undefined;
  active?: boolean;
  onToggle: (value: string | null) => void;
  /** Names the column in the accessible label ("Filter by recruiter Asha R"). */
  columnLabel: string;
  className?: string;
  children?: ReactNode;
  /**
   * What the em-dash MEANS in this column, on hover.
   *
   * A blank cell is ambiguous in exactly the way that gets a working feature
   * reported as broken: "we failed to load this" and "there is genuinely
   * nothing here" look identical. Columns where the empty state is a real,
   * expected answer — a call placed with no requirement attached — should say
   * so rather than leave the reader to guess which of the two they are seeing.
   */
  emptyHint?: string;
}) {
  if (!value) {
    return (
      <span
        className={cn('text-muted-foreground/50', emptyHint && 'cursor-help')}
        title={emptyHint}
      >
        —
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        // Host rows are often clickable themselves (row → detail). Filtering is
        // not navigating, so it must not also trigger the row.
        e.stopPropagation();
        onToggle(active ? null : value);
      }}
      aria-pressed={active}
      title={active ? `Clear the ${columnLabel} filter` : `Filter by ${columnLabel} ${value}`}
      aria-label={active ? `Clear the ${columnLabel} filter, ${value}` : `Filter by ${columnLabel} ${value}`}
      className={cn(
        // A DOTTED underline at rest, not just on hover. These cells have
        // filtered-to-this-value since they shipped, and nobody found them: at
        // rest they were styled exactly like the plain text in the columns
        // beside them, so the only hint was a hover state you have to already
        // suspect to go looking for. The dotted rule is the conventional "this
        // word does something" mark and costs no layout; hover promotes it to
        // solid to confirm the target.
        'max-w-full truncate text-left rounded-control decoration-dotted underline-offset-4',
        'underline hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
        active
          ? 'text-primary font-medium decoration-primary/60'
          : 'text-muted-foreground decoration-muted-foreground/40 hover:text-foreground',
        className,
      )}
    >
      {children ?? value}
    </button>
  );
}

/**
 * A cell value that OPENS the record it names.
 *
 * Sibling of {@link FilterValueButton}, and deliberately right beside it: a
 * table's text columns can do two different things when clicked — narrow the
 * list to this value, or go to this thing — and the two must not look alike.
 * Keeping both here makes that rule visible in one place instead of leaving it
 * to whatever className each page happens to write.
 *
 * So the affordances are split on purpose:
 *   FilterValueButton   muted text, DOTTED underline  → "filter by this"
 *   RecordLinkButton    primary text, SOLID underline → "open this"
 *
 * A real `button` rather than an anchor: the common case is a side panel, not a
 * navigation, and an `<a href>` that does not navigate breaks middle-click and
 * "open in new tab" in a way users notice. Pages that genuinely route somewhere
 * should use a `Link` instead of this.
 */
export function RecordLinkButton({ value, onOpen, recordLabel, className, children }: {
  /** The text to show. Nothing renders when it is empty. */
  value: string | null | undefined;
  onOpen: () => void;
  /** Names the thing in the accessible label ("Open MD Zishan's profile"). */
  recordLabel: string;
  className?: string;
  children?: ReactNode;
}) {
  if (!value) return <span className="text-muted-foreground/50">—</span>;
  const label = `Open ${value}'s ${recordLabel}`;
  return (
    <button
      type="button"
      onClick={(e) => {
        // Host rows are often clickable themselves. This is a more specific
        // target inside one, so it must not also fire the row's own handler.
        e.stopPropagation();
        onOpen();
      }}
      title={label}
      // A button whose only content is a name has that NAME as its accessible
      // name, so a screen-reader user hears "MD Zishan, button" and nothing
      // about what it does. Still contains the visible text (WCAG 2.5.3).
      aria-label={label}
      className={cn(
        'max-w-full truncate rounded-control text-left font-medium',
        'text-primary underline decoration-primary/40 underline-offset-4 hover:decoration-primary',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
        className,
      )}
    >
      {children ?? value}
    </button>
  );
}

/**
 * A dismissible "you are filtered by X" chip for the toolbar above a table.
 *
 * Pairs with FilterValueButton and earns its place because of it: a filter set
 * by clicking a value in the table leaves no trace in the toolbar, so without
 * this the only evidence is a tinted funnel in a header the user may never look
 * at. Naming every applied filter in one row — each individually removable — is
 * what keeps click-to-filter from becoming a state you can get stuck in.
 */
export function ActiveFilterChip({ label, value, onClear }: {
  label: string;
  value: string;
  onClear: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClear}
      // "Remove", not "Clear" — an active FilterValueButton in the table body
      // also clears the same filter, and two controls whose accessible names
      // differ only by case are indistinguishable to a screen-reader user
      // navigating by control list.
      aria-label={`Remove filter ${label} ${value}`}
      title={`Remove the ${label} filter`}
      className="inline-flex max-w-[16rem] items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-xs text-primary hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
    >
      <span className="truncate">{label}: {value}</span>
      <X aria-hidden="true" className="h-3 w-3 shrink-0" />
    </button>
  );
}

/**
 * Two-ended numeric range filter ("Min → Max").
 *
 * Lifted out of the Applications page, which invented it, so the pipeline's
 * headers get the same control rather than a near-copy — the same reason
 * ColumnHeaderContent/Cell live here. Both ends are optional: an empty Min is
 * "no lower bound", not zero.
 */
export function RangeFilterPopover({ label, min, max, onMinChange, onMaxChange, onApply, onClear, suffix }: {
  label: string;
  min: string; max: string;
  onMinChange: (v: string) => void; onMaxChange: (v: string) => void;
  onApply: () => void; onClear: () => void;
  suffix?: string;
}) {
  return (
    <div className="space-y-3">
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Input
            type="number"
            placeholder="Min"
            value={min}
            onChange={(e) => onMinChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onApply(); }}
          />
        </div>
        <span className="text-muted-foreground">→</span>
        <div className="flex-1">
          <Input
            type="number"
            placeholder="Max"
            value={max}
            onChange={(e) => onMaxChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onApply(); }}
          />
        </div>
      </div>
      {suffix && <div className="text-xs text-muted-foreground">{suffix}</div>}
      <div className="flex justify-between gap-2">
        <Button size="sm" variant="ghost" onClick={onClear}>Clear</Button>
        <Button size="sm" onClick={onApply}>Apply</Button>
      </div>
    </div>
  );
}
